'use client';

import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import Script from 'next/script';
import styles from '../styles/CandidatePhoto.module.css';
import { computeDescriptorsFromImages, preloadFaceModels } from '@/lib/face-descriptors';

const ROLL_THRESHOLD = 40;
const PITCH_THRESHOLD = 20;
const YAW_THRESHOLD = 20;
const DEVIATION_THRESHOLD = 15;
const SEGMENT_INSTRUCTIONS = [
  'Tilt your head to bottom right',
  'Tilt your head to bottom left',
  'Tilt your head to top left',
  'Tilt your head to top right',
];
const PREFERRED_SEGMENT_ORDER = [2, 3, 0, 1];
const FACE_OVAL_MARGIN = 0.2;
const X_RADIUS_PERCENTAGE = 0.35;
const Y_RADIUS_PERCENTAGE = 0.35;
const TILT_FRAME_COUNT = 3;
const segments = 4;
const gap = 10;
const angleStep = (2 * Math.PI) / segments;
const TICKS_PER_SEGMENT = 16;
const CIRCULAR_SEGMENT_OFFSET = 5;
const SEGMENT_FILL_SPEED = 0.075;
const MAX_GREEN_TICK_LENGTH = 14;
const MAX_GRAY_TICK_LENGTH = 10;

const INSTRUCTION_TITLE = [
  'Two steps: position, then tilt',
  'Lighting is critical',
  'Stay inside the circle',
];
const INSTRUCTION_DESCRIPTION: ReactNode[] = [
  <>
    First, hold your face straight inside the square frame until it is captured. Then slowly tilt your head to{' '}
    <strong>top left, top right, bottom right,</strong> and <strong>bottom left</strong> to fill each
    one. A segment turning{' '}
    <span className={styles.instructionGreenText}>green</span> means it is saved. If it turns{' '}
    <span className={styles.instructionYellowText}>yellow</span>, your angle is right but the
    lighting is too poor to capture. Improve the lighting and try again.
  </>,
  'Your face must be evenly lit from the front. Bright windows or lamps directly behind you create backlight that prevents the scan from working. Find a well-lit spot facing a steady light source so your face is clearly and uniformly visible.',
  'Your face must stay fully inside the circle throughout the scan. If any part of your face drifts outside, the current tilt will not be captured until you re-center. The scan resumes automatically once you are back inside.',
];
const INSTRUCTION_IMAGE_SRC_MAP = [
  '/images/instruction-4.png',
  '/images/instruction_light.png',
  '/images/instruction-2.png',
];
const INSTRUCTION_PREVIEW_FRAMES: { src: string; segment: number | null; instruction: string }[] = [
  { src: '/images/face_front.png', segment: null, instruction: 'Rotate your head in circle' },
  // Segment mapping follows the same canvas angle system used in live capture:
  // 0: bottom-right, 1: bottom-left, 2: top-left, 3: top-right.
  { src: '/images/face_top_left.png', segment: 2, instruction: 'Tilt your head to top left' },
  { src: '/images/face_top_right.png', segment: 3, instruction: 'Tilt your head to top right' },
  { src: '/images/face_bottom_right.png', segment: 0, instruction: 'Tilt your head to bottom right' },
  { src: '/images/face_bottom_left.png', segment: 1, instruction: 'Tilt your head to bottom left' },
];
const INSTRUCTION_PREVIEW_PAGE_INDEX = 0;
const INSTRUCTION_POSITION_DEMO_PAGE_INDEX = 2;
const INSTRUCTION_PREVIEW_POSITION_HOLD_MS = 1800;
const INSTRUCTION_PREVIEW_TICK_INTERVAL_MS = 95;
const INSTRUCTION_PREVIEW_PROGRESS_STEP = 0.05;
const INSTRUCTION_PREVIEW_CIRCLE_SCALE = 1.08;
const INSTRUCTION_PREVIEW_POSITION_MASK_SCALE = 1.08;
const INSTRUCTION_PREVIEW_POSITION_CORNER_SCALE = 0.86;

interface Stage2CameraProps {
  onComplete?: (images: string[]) => void | Promise<void>;
  onBack?: () => void;
  onCaptureComplete?: (images: string[], descriptors?: number[][]) => void;
  isLoading?: boolean;
}

export default function Stage2Camera({ onComplete, onBack, onCaptureComplete, isLoading = false }: Stage2CameraProps) {
  const [currentInstructionPage, setCurrentInstructionPage] = useState(0);
  const [showGetStarted, setShowGetStarted] = useState(true);
  const [showInstructions, setShowInstructions] = useState(false);
  const [cameraLoading, setCameraLoading] = useState(false);
  const [faceModelLoading, setFaceModelLoading] = useState(false);
  const [faceModelError, setFaceModelError] = useState<string | null>(null);
  const [showVideo, setShowVideo] = useState(false);
  const [captureComplete, setCaptureComplete] = useState(false);
  const [scriptsLoaded, setScriptsLoaded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [instructionPreviewPhase, setInstructionPreviewPhase] = useState<'position' | 'rotate'>(
    'position',
  );
  const [instructionPreviewFrame, setInstructionPreviewFrame] = useState(0);
  const [instructionPreviewProgress, setInstructionPreviewProgress] = useState(0);
  const [instructionPreviewIsPlaying, setInstructionPreviewIsPlaying] = useState(false);
  const [instructionPreviewCompletedSegments, setInstructionPreviewCompletedSegments] = useState<
    number[]
  >([]);
  const [instructionPreviewComplete, setInstructionPreviewComplete] = useState(false);
  const [instructionPreviewAssetsReady, setInstructionPreviewAssetsReady] = useState(false);
  const instructionPreviewCanvasRef = useRef<HTMLCanvasElement>(null);
  const instructionPositionDemoCanvasRef = useRef<HTMLCanvasElement>(null);

  // Reset isSubmitting when parent's loading completes
  useEffect(() => {
    if (!isLoading && isSubmitting) {
      // Small delay to ensure smooth transition
      const timer = setTimeout(() => {
        setIsSubmitting(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isLoading, isSubmitting]);

  const instructionPreviewFrameRef = useRef(0);
  const instructionPreviewIntervalRef = useRef<number | null>(null);
  const instructionPreviewPhaseTimeoutRef = useRef<number | null>(null);

  const stopInstructionPreview = useCallback(() => {
    if (instructionPreviewPhaseTimeoutRef.current) {
      window.clearTimeout(instructionPreviewPhaseTimeoutRef.current);
      instructionPreviewPhaseTimeoutRef.current = null;
    }
    if (instructionPreviewIntervalRef.current) {
      window.clearInterval(instructionPreviewIntervalRef.current);
      instructionPreviewIntervalRef.current = null;
    }
  }, []);

  const replayInstructionPreview = useCallback(() => {
    if (!instructionPreviewAssetsReady) return;
    stopInstructionPreview();
    instructionPreviewFrameRef.current = 0;
    setInstructionPreviewIsPlaying(true);
    setInstructionPreviewPhase('position');
    setInstructionPreviewFrame(0);
    setInstructionPreviewProgress(0);
    setInstructionPreviewCompletedSegments([]);
    setInstructionPreviewComplete(false);

    instructionPreviewPhaseTimeoutRef.current = window.setTimeout(() => {
      setInstructionPreviewPhase('rotate');
      instructionPreviewIntervalRef.current = window.setInterval(() => {
        setInstructionPreviewProgress((prev) => {
          const next = prev + INSTRUCTION_PREVIEW_PROGRESS_STEP;
          if (next < 1) {
            return next;
          }

          const currentFrame = instructionPreviewFrameRef.current;
          const currentSegment = INSTRUCTION_PREVIEW_FRAMES[currentFrame]?.segment;
          if (currentSegment !== null && currentSegment !== undefined) {
            setInstructionPreviewCompletedSegments((prevSegments) =>
              prevSegments.includes(currentSegment) ? prevSegments : [...prevSegments, currentSegment],
            );
          }
          const isLastFrame = currentFrame >= INSTRUCTION_PREVIEW_FRAMES.length - 1;
          if (isLastFrame) {
            stopInstructionPreview();
            setInstructionPreviewIsPlaying(false);
            setInstructionPreviewComplete(true);
            return 1;
          }

          const nextFrame = currentFrame + 1;
          instructionPreviewFrameRef.current = nextFrame;
          setInstructionPreviewFrame(nextFrame);
          return 0;
        });
      }, INSTRUCTION_PREVIEW_TICK_INTERVAL_MS);
    }, INSTRUCTION_PREVIEW_POSITION_HOLD_MS);
  }, [instructionPreviewAssetsReady, stopInstructionPreview]);

  useEffect(() => {
    let cancelled = false;
    const preloadImages = async () => {
      const uniqueSources = Array.from(new Set(INSTRUCTION_PREVIEW_FRAMES.map((frame) => frame.src)));
      await Promise.all(
        uniqueSources.map(
          (src) =>
            new Promise<void>((resolve, reject) => {
              const img = new Image();
              img.onload = () => resolve();
              img.onerror = () => reject(new Error(`Failed to preload ${src}`));
              img.src = src;
            }),
        ),
      );
      if (!cancelled) {
        setInstructionPreviewAssetsReady(true);
      }
    };

    void preloadImages().catch((error) => {
      console.warn('[Stage2Camera] Failed to preload instruction preview assets', error);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!showInstructions || currentInstructionPage !== INSTRUCTION_PREVIEW_PAGE_INDEX) {
      stopInstructionPreview();
      setInstructionPreviewIsPlaying(false);
      return;
    }
    if (!instructionPreviewAssetsReady) return;
    replayInstructionPreview();
    return () => {
      stopInstructionPreview();
    };
  }, [
    showInstructions,
    currentInstructionPage,
    instructionPreviewAssetsReady,
    replayInstructionPreview,
    stopInstructionPreview,
  ]);

  useEffect(() => {
    if (!showInstructions || currentInstructionPage !== INSTRUCTION_PREVIEW_PAGE_INDEX) return;

    const canvas = instructionPreviewCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const displayWidth = Math.max(1, canvas.clientWidth);
    const displayHeight = Math.max(1, canvas.clientHeight);
    const dpr = window.devicePixelRatio || 1;
    const nextWidth = Math.round(displayWidth * dpr);
    const nextHeight = Math.round(displayHeight * dpr);
    if (canvas.width !== nextWidth) canvas.width = nextWidth;
    if (canvas.height !== nextHeight) canvas.height = nextHeight;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const size = Math.min(displayWidth, displayHeight);
    const offsetX = (displayWidth - size) / 2;
    const offsetY = (displayHeight - size) / 2;
    const centerX = offsetX + size / 2;
    const centerY = offsetY + size / 2;
    const radiusX = (size * X_RADIUS_PERCENTAGE - gap) * INSTRUCTION_PREVIEW_CIRCLE_SCALE;
    const radiusY = (size * Y_RADIUS_PERCENTAGE - gap) * INSTRUCTION_PREVIEW_CIRCLE_SCALE;

    ctx.clearRect(0, 0, displayWidth, displayHeight);

    if (instructionPreviewPhase === 'position') {
      const cornerRadius = 10;
      const edgeGap = 46;
      const g = edgeGap;
      const r = cornerRadius;
      const maskHalfWidth = INSTRUCTION_PREVIEW_POSITION_MASK_SCALE * radiusX;
      const maskLeft = centerX - maskHalfWidth;
      const maskTop = centerY - maskHalfWidth;
      const maskRight = centerX + maskHalfWidth;
      const maskBottom = centerY + maskHalfWidth;
      const cornerHalfWidth = INSTRUCTION_PREVIEW_POSITION_CORNER_SCALE * radiusX;
      const left = centerX - cornerHalfWidth;
      const top = centerY - cornerHalfWidth;
      const right = centerX + cornerHalfWidth;
      const bottom = centerY + cornerHalfWidth;

      // Draw mask first so corner brackets can be drawn above it for visibility.
      const displacement = 0;
      ctx.beginPath();
      ctx.rect(0, 0, displayWidth, displayHeight);
      ctx.rect(
        maskLeft - displacement,
        maskTop - displacement,
        maskRight - maskLeft + displacement * 2,
        maskBottom - maskTop + displacement * 2,
      );
      ctx.fillStyle = 'rgba(0,0,0,1)';
      ctx.fill('evenodd');

      ctx.beginPath();
      ctx.moveTo(left + r, top);
      ctx.arcTo(left, top, left, top + r, r);
      ctx.moveTo(left, top + r);
      ctx.lineTo(left, centerY - g);
      ctx.moveTo(left, centerY + g);
      ctx.lineTo(left, bottom - r);
      ctx.arcTo(left, bottom, left + r, bottom, r);
      ctx.lineTo(centerX - g, bottom);
      ctx.moveTo(centerX + g, bottom);
      ctx.lineTo(right - r, bottom);
      ctx.arcTo(right, bottom, right, bottom - r, r);
      ctx.lineTo(right, centerY + g);
      ctx.moveTo(right, centerY - g);
      ctx.lineTo(right, top + r);
      ctx.arcTo(right, top, right - r, top, r);
      ctx.lineTo(centerX + g, top);
      ctx.moveTo(centerX - g, top);
      ctx.lineTo(left + r, top);
      ctx.shadowColor = 'rgba(0, 0, 0, 0.55)';
      ctx.shadowBlur = 2;
      ctx.strokeStyle = '#FFFFFF';
      ctx.lineWidth = 5;
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      ctx.stroke();
      ctx.shadowBlur = 0;
      return;
    }

    ctx.beginPath();
    ctx.rect(0, 0, displayWidth, displayHeight);
    ctx.ellipse(centerX, centerY, radiusX + gap, radiusY + gap, 0, 0, 2 * Math.PI);
    ctx.fillStyle = 'rgba(0,0,0,1)';
    ctx.fill('evenodd');

    const baseOuterRxWithOffset = radiusX + gap + CIRCULAR_SEGMENT_OFFSET;
    const baseOuterRyWithOffset = radiusY + gap + CIRCULAR_SEGMENT_OFFSET;

    for (let i = 0; i < TICKS_PER_SEGMENT * segments; i += 1) {
      const centerFrac = (i + 0.5) / TICKS_PER_SEGMENT;
      const angle = centerFrac * angleStep;

      const innerRx = baseOuterRxWithOffset;
      const innerRy = baseOuterRyWithOffset;
      const outerRx = baseOuterRxWithOffset + MAX_GREEN_TICK_LENGTH * 0.5;
      const outerRy = baseOuterRyWithOffset + MAX_GREEN_TICK_LENGTH * 0.5;

      const innerX = centerX + innerRx * Math.cos(angle);
      const innerY = centerY + innerRy * Math.sin(angle);
      const outerX = centerX + outerRx * Math.cos(angle);
      const outerY = centerY + outerRy * Math.sin(angle);

      ctx.beginPath();
      ctx.moveTo(innerX, innerY);
      ctx.lineTo(outerX, outerY);
      ctx.strokeStyle = '#4b5563';
      ctx.lineWidth = 3;
      ctx.lineCap = 'round';
      ctx.stroke();
    }

    // Keep previously completed segments fully green until replay.
    for (let i = 0; i < TICKS_PER_SEGMENT * segments; i += 1) {
      const segmentIndex = Math.floor(i / TICKS_PER_SEGMENT);
      if (!instructionPreviewCompletedSegments.includes(segmentIndex)) continue;

      const centerFrac = (i + 0.5) / TICKS_PER_SEGMENT;
      const angle = centerFrac * angleStep;
      const innerRx = baseOuterRxWithOffset;
      const innerRy = baseOuterRyWithOffset;
      const outerRx = baseOuterRxWithOffset + MAX_GREEN_TICK_LENGTH;
      const outerRy = baseOuterRyWithOffset + MAX_GREEN_TICK_LENGTH;

      const innerX = centerX + innerRx * Math.cos(angle);
      const innerY = centerY + innerRy * Math.sin(angle);
      const outerX = centerX + outerRx * Math.cos(angle);
      const outerY = centerY + outerRy * Math.sin(angle);

      ctx.beginPath();
      ctx.moveTo(innerX, innerY);
      ctx.lineTo(outerX, outerY);
      ctx.strokeStyle = '#22C55E';
      ctx.lineWidth = 4;
      ctx.lineCap = 'round';
      ctx.stroke();
    }

    const activeFrame = INSTRUCTION_PREVIEW_FRAMES[instructionPreviewFrame];
    if (activeFrame.segment === null || instructionPreviewCompletedSegments.includes(activeFrame.segment))
      return;
    const startAngle = activeFrame.segment * angleStep;

    for (let i = 0; i < TICKS_PER_SEGMENT; i += 1) {
      const segmentStartFrac = i / TICKS_PER_SEGMENT;
      const local = (instructionPreviewProgress - segmentStartFrac) * TICKS_PER_SEGMENT;
      const lengthFactor = Math.min(Math.max(local, 0), 1);
      if (lengthFactor <= 0) continue;

      const centerFrac = (i + 0.5) / TICKS_PER_SEGMENT;
      const angle = startAngle + centerFrac * angleStep;

      const innerRx = baseOuterRxWithOffset;
      const innerRy = baseOuterRyWithOffset;
      const outerRx = baseOuterRxWithOffset + MAX_GREEN_TICK_LENGTH * lengthFactor;
      const outerRy = baseOuterRyWithOffset + MAX_GREEN_TICK_LENGTH * lengthFactor;

      const innerX = centerX + innerRx * Math.cos(angle);
      const innerY = centerY + innerRy * Math.sin(angle);
      const outerX = centerX + outerRx * Math.cos(angle);
      const outerY = centerY + outerRy * Math.sin(angle);

      ctx.beginPath();
      ctx.moveTo(innerX, innerY);
      ctx.lineTo(outerX, outerY);
      ctx.strokeStyle = '#22C55E';
      ctx.lineWidth = 4;
      ctx.lineCap = 'round';
      ctx.stroke();
    }
  }, [
    showInstructions,
    currentInstructionPage,
    instructionPreviewPhase,
    instructionPreviewFrame,
    instructionPreviewProgress,
    instructionPreviewCompletedSegments,
  ]);
  useEffect(() => {
    if (!showInstructions || currentInstructionPage !== INSTRUCTION_POSITION_DEMO_PAGE_INDEX) return;

    const canvas = instructionPositionDemoCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const displayWidth = Math.max(1, canvas.clientWidth);
    const displayHeight = Math.max(1, canvas.clientHeight);
    const dpr = window.devicePixelRatio || 1;
    const nextWidth = Math.round(displayWidth * dpr);
    const nextHeight = Math.round(displayHeight * dpr);
    if (canvas.width !== nextWidth) canvas.width = nextWidth;
    if (canvas.height !== nextHeight) canvas.height = nextHeight;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const size = Math.min(displayWidth, displayHeight);
    const offsetX = (displayWidth - size) / 2;
    const offsetY = (displayHeight - size) / 2;
    const centerX = offsetX + size / 2;
    const centerY = offsetY + size / 2;
    const radiusX = (size * X_RADIUS_PERCENTAGE - gap) * INSTRUCTION_PREVIEW_CIRCLE_SCALE;
    const radiusY = (size * Y_RADIUS_PERCENTAGE - gap) * INSTRUCTION_PREVIEW_CIRCLE_SCALE;

    ctx.clearRect(0, 0, displayWidth, displayHeight);
    ctx.beginPath();
    ctx.rect(0, 0, displayWidth, displayHeight);
    ctx.ellipse(centerX, centerY, radiusX + gap, radiusY + gap, 0, 0, 2 * Math.PI);
    ctx.fillStyle = 'rgba(0,0,0,1)';
    ctx.fill('evenodd');

    const baseOuterRxWithOffset = radiusX + gap + CIRCULAR_SEGMENT_OFFSET;
    const baseOuterRyWithOffset = radiusY + gap + CIRCULAR_SEGMENT_OFFSET;
    for (let i = 0; i < TICKS_PER_SEGMENT * segments; i += 1) {
      const centerFrac = (i + 0.5) / TICKS_PER_SEGMENT;
      const angle = centerFrac * angleStep;
      const innerRx = baseOuterRxWithOffset;
      const innerRy = baseOuterRyWithOffset;
      const outerRx = baseOuterRxWithOffset + MAX_GRAY_TICK_LENGTH;
      const outerRy = baseOuterRyWithOffset + MAX_GRAY_TICK_LENGTH;
      const innerX = centerX + innerRx * Math.cos(angle);
      const innerY = centerY + innerRy * Math.sin(angle);
      const outerX = centerX + outerRx * Math.cos(angle);
      const outerY = centerY + outerRy * Math.sin(angle);

      ctx.beginPath();
      ctx.moveTo(innerX, innerY);
      ctx.lineTo(outerX, outerY);
      ctx.strokeStyle = '#4b5563';
      ctx.lineWidth = 3;
      ctx.lineCap = 'round';
      ctx.stroke();
    }
  }, [showInstructions, currentInstructionPage]);

  const videoRef = useRef<HTMLVideoElement>(null);
  const overlayRef = useRef<HTMLCanvasElement>(null);
  const overlayBarRef = useRef<HTMLCanvasElement>(null);
  const overlayBarIndicatorRef = useRef<HTMLCanvasElement>(null);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const videoInstructionsContainerRef = useRef<HTMLDivElement>(null);
  const videoInstructionImageRef = useRef<HTMLImageElement>(null);
  const videoInstructionIconRef = useRef<HTMLDivElement>(null);
  const videoInstructionTextRef = useRef<HTMLDivElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const cameraRef = useRef<any>(null);
  const faceMeshRef = useRef<any>(null);
  const centerXRef = useRef<number>(0);
  const centerYRef = useRef<number>(0);
  const radiusXRef = useRef<number>(0);
  const radiusYRef = useRef<number>(0);
  const videoFrameRateRef = useRef<number>(30);
  const segmentFrameCountRef = useRef<number[]>(Array(segments + 1).fill(0));
  const filledSegmentsRef = useRef<Set<number>>(new Set());
  const savedSegmentsRef = useRef<Set<number>>(new Set());
  const cautionSegmentRef = useRef<number | null>(null);
  const currentTiltSegmentRef = useRef<number | null>(null);
  const pendingCaptureSegmentsRef = useRef<Set<number>>(new Set());
  const positionFaceCompleteRef = useRef<boolean>(false);
  const positionFaceDescriptorPendingRef = useRef<boolean>(false);
  const positionFaceDescriptorFailedRef = useRef<boolean>(false);
  const descriptorWarmupPromiseRef = useRef<Promise<void> | null>(null);
  const capturedFramesRef = useRef<string[]>([]);
  const capturedDescriptorsRef = useRef<number[][]>([]);
  const faceIdCaptureWarningRef = useRef<boolean>(false);
  const FACE_ID_CAPTURE_WARNING_MIN_MS = 1000;
  const faceIdCaptureFailureAtRef = useRef<number | null>(null);
  const faceIdCaptureWarningClearTimeoutRef = useRef<number | null>(null);

  const markFaceIdCaptureFailed = () => {
    faceIdCaptureWarningRef.current = true;
    faceIdCaptureFailureAtRef.current = Date.now();

    if (faceIdCaptureWarningClearTimeoutRef.current) {
      window.clearTimeout(faceIdCaptureWarningClearTimeoutRef.current);
    }

    faceIdCaptureWarningClearTimeoutRef.current = window.setTimeout(() => {
      faceIdCaptureWarningRef.current = false;
      faceIdCaptureFailureAtRef.current = null;
      faceIdCaptureWarningClearTimeoutRef.current = null;
    }, FACE_ID_CAPTURE_WARNING_MIN_MS);
  };

  const clearFaceIdCaptureWarningNow = () => {
    if (faceIdCaptureWarningClearTimeoutRef.current) {
      window.clearTimeout(faceIdCaptureWarningClearTimeoutRef.current);
      faceIdCaptureWarningClearTimeoutRef.current = null;
    }
    faceIdCaptureWarningRef.current = false;
    faceIdCaptureFailureAtRef.current = null;
  };
  const getNextDirectionInstruction = (): string => {
    for (const segment of PREFERRED_SEGMENT_ORDER) {
      if (!savedSegmentsRef.current.has(segment)) {
        return SEGMENT_INSTRUCTIONS[segment];
      }
    }
    return SEGMENT_INSTRUCTIONS[PREFERRED_SEGMENT_ORDER[0]];
  };
  const positionFaceImgRef = useRef<HTMLImageElement | null>(null);
  const moveFaceImgRef = useRef<HTMLImageElement | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  // Check for scripts and initialize FaceMesh
  useEffect(() => {
    const checkScripts = () => {
      if (
        (window as any).FaceMesh &&
        (window as any).Camera &&
        (window as any).drawConnectors &&
        !faceMeshRef.current
      ) {
        console.log('Initializing FaceMesh...');
        const faceMesh = new (window as any).FaceMesh({
          locateFile: (file: string) => {
            return `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${file}`;
          },
        });

        faceMesh.setOptions({
          maxNumFaces: 1,
          refineLandmarks: true,
          minDetectionConfidence: 0.5,
          minTrackingConfidence: 0.5,
        });

        faceMesh.onResults(onResults);
        faceMeshRef.current = faceMesh;

        // Preload images
        const moveImg = new Image();
        moveImg.src = '/images/move-face-black.png';
        moveFaceImgRef.current = moveImg;

        const positionImg = new Image();
        positionImg.src = '/images/position-face-black.png';
        positionFaceImgRef.current = positionImg;

        console.log('FaceMesh initialized successfully');
        setScriptsLoaded(true);
      }
    };

    // Check immediately
    checkScripts();

    // Also check periodically in case scripts load after component mounts
    const interval = setInterval(() => {
      if (!faceMeshRef.current) {
        checkScripts();
      } else {
        clearInterval(interval);
      }
    }, 500);

    return () => clearInterval(interval);
  }, []);

  const startCamera = async () => {
    try {
      console.log('Starting camera...');
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user' },
        audio: false,
      });

      const video = videoRef.current;
      if (!video) {
        console.error('Video element not found');
        setCameraLoading(false);
        return;
      }

      console.log('Stream obtained, setting srcObject...');
      video.srcObject = stream;
      streamRef.current = stream;

      const track = stream.getVideoTracks()[0];
      const settings = track.getSettings();
      videoFrameRateRef.current = settings.frameRate || 30;

      video.onloadedmetadata = () => {
        console.log('Video metadata loaded, dimensions:', video.videoWidth, video.videoHeight);
        centerXRef.current = video.videoWidth / 2;
        centerYRef.current = video.videoHeight / 2;
        radiusXRef.current =
          Math.min(video.videoWidth, video.videoHeight) * X_RADIUS_PERCENTAGE - gap;
        radiusYRef.current =
          Math.min(video.videoWidth, video.videoHeight) * Y_RADIUS_PERCENTAGE - gap;

        // Set canvas dimensions
        if (overlayRef.current) {
          overlayRef.current.width = video.videoWidth;
          overlayRef.current.height = video.videoHeight;
        }
        if (overlayBarRef.current) {
          overlayBarRef.current.width = video.videoWidth;
          overlayBarRef.current.height = video.videoHeight;
        }
        if (overlayBarIndicatorRef.current) {
          overlayBarIndicatorRef.current.width = video.videoWidth;
          overlayBarIndicatorRef.current.height = video.videoHeight;
        }

        // Apply transforms exactly like original selfie.js
        const translateY = 0; // keep face centered
        const isMobile = window.innerWidth <= 768;
        const isShortViewport = window.innerHeight <= 720;
        const desktopScale = isShortViewport ? 1.25 : 1.75;
        const overlayScale = isMobile ? 1.5 : desktopScale;

        if (video) {
          video.style.transform = `scaleX(-1) translateY(${translateY}px)`;
          video.style.width = `${video.videoWidth}px`;
          video.style.height = `${video.videoHeight}px`;
          video.style.maxWidth = '100%';
          video.style.maxHeight = '100%';
          video.style.objectFit = 'contain';
          video.style.scale = String(overlayScale);
        }
        if (overlayRef.current) {
          overlayRef.current.style.width = `${video.videoWidth}px`;
          overlayRef.current.style.height = `${video.videoHeight}px`;
          overlayRef.current.style.maxWidth = '100%';
          overlayRef.current.style.maxHeight = '100%';
          overlayRef.current.style.scale = String(overlayScale);
          overlayRef.current.style.transform = `translateY(${translateY}px)`;
          overlayRef.current.style.transformOrigin = 'center center';
        }
        if (overlayBarRef.current) {
          overlayBarRef.current.style.width = `${video.videoWidth}px`;
          overlayBarRef.current.style.height = `${video.videoHeight}px`;
          overlayBarRef.current.style.maxWidth = '100%';
          overlayBarRef.current.style.maxHeight = '100%';
          overlayBarRef.current.style.scale = String(overlayScale);
          overlayBarRef.current.style.transform = `translateY(${translateY}px)`;
          overlayBarRef.current.style.transformOrigin = 'center center';
        }
        if (overlayBarIndicatorRef.current) {
          overlayBarIndicatorRef.current.style.width = `${video.videoWidth}px`;
          overlayBarIndicatorRef.current.style.height = `${video.videoHeight}px`;
          overlayBarIndicatorRef.current.style.maxWidth = '100%';
          overlayBarIndicatorRef.current.style.maxHeight = '100%';
          overlayBarIndicatorRef.current.style.scale = String(overlayScale);
          overlayBarIndicatorRef.current.style.transform = `translateY(${translateY}px)`;
          overlayBarIndicatorRef.current.style.transformOrigin = 'center center';
        }

        // Position video instructions container (absolute inside videoContainerInner)
        if (videoInstructionsContainerRef.current) {
          videoInstructionsContainerRef.current.style.top = 'auto';
          videoInstructionsContainerRef.current.style.bottom = '1.25rem';
        }

        // Start MediaPipe camera - wait a bit for scripts if needed
        const initCamera = () => {
          if (faceMeshRef.current && (window as any).Camera) {
            console.log('Initializing MediaPipe Camera...');
            const camera = new (window as any).Camera(video, {
              onFrame: async () => {
                await faceMeshRef.current.send({ image: video });

                // Warm up face-api descriptor inference using a static face image so all
                // three networks (SSD, landmarks, recognition) compile even when the live
                // camera has no face visible yet.
                if (!descriptorWarmupPromiseRef.current) {
                  descriptorWarmupPromiseRef.current = (async () => {
                    try {
                      await computeDescriptorsFromImages(['/images/face_warmup.jpg']);
                      console.log('[Stage2Camera] Descriptor warmup success');
                    } catch (e) {
                      console.warn('[Stage2Camera] Descriptor warmup failed', e);
                    }
                  })();
                }
                await descriptorWarmupPromiseRef.current;

                setShowVideo(true);
                setCameraLoading(false);
              },
            });

            camera.start();
            cameraRef.current = camera;
            console.log('Camera started');
          } else {
            console.log('Waiting for MediaPipe scripts to load...');
            // Retry after a short delay
            setTimeout(() => {
              if (faceMeshRef.current && (window as any).Camera) {
                initCamera();
              } else {
                console.error('FaceMesh or Camera not available after retry');
                setCameraLoading(false);
              }
            }, 1000);
          }
        };

        initCamera();
      };

      video.onerror = (error) => {
        console.error('Video error:', error);
        setCameraLoading(false);
      };
    } catch (error) {
      console.error('Error accessing front camera:', error);
      alert('Unable to access front camera. Please check permissions.');
      setCameraLoading(false);
    }
  };

  const getFaceAngles = (landmarks: any[]) => {
    const LEFT_EYE_INDEX = 33;
    const RIGHT_EYE_INDEX = 263;
    const NOSE_TIP_INDEX = 1;
    const CHIN_INDEX = 152;

    const leftEye = landmarks[LEFT_EYE_INDEX];
    const rightEye = landmarks[RIGHT_EYE_INDEX];
    const nose = landmarks[NOSE_TIP_INDEX];
    const chin = landmarks[CHIN_INDEX];

    const dx = rightEye.x - leftEye.x;
    const dy = rightEye.y - leftEye.y;
    const rollRad = Math.atan2(dy, dx);
    const roll = (rollRad * 180) / Math.PI;

    const eyeMidX = (leftEye.x + rightEye.x) / 2;
    const eyeMidZ = (leftEye.z + rightEye.z) / 2;
    const yawRad = Math.atan2(nose.x - eyeMidX, -(nose.z - eyeMidZ));
    const yaw = (yawRad * 180) / Math.PI;

    const pitchRad = Math.atan2(chin.y - nose.y, -(chin.z - nose.z));
    const calculatedPitch = (pitchRad * 180) / Math.PI;
    const unNormalizedPitch = calculatedPitch - 110;
    const pitch = ((unNormalizedPitch + 180) % 360) - 180;

    return { roll, pitch, yaw };
  };

  const isFrameDark = (threshold = 70): boolean => {
    const video = videoRef.current;
    if (!video) return false;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return false;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;

    let totalLuminance = 0;
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
      totalLuminance += luminance;
    }

    const avgLuminance = totalLuminance / (data.length / 4);
    return avgLuminance < threshold;
  };

  const setInstructionText = (text: string, isDarkEnvironment = false) => {
    if (!videoInstructionTextRef.current) return;
    if (isDarkEnvironment) {
      videoInstructionTextRef.current.innerText = text;
      return;
    }
    if (faceIdCaptureWarningRef.current) {
      videoInstructionTextRef.current.innerText =
        'Face ID capture failed. Please make sure you are in a well-lit room.';
      return;
    }
    videoInstructionTextRef.current.innerText = text;
  };

  const saveFrame = (shouldStore = true): string | null => {
    const video = videoRef.current;
    if (!video) return null;

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext('2d');
    if (context) {
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      const dataURL = canvas.toDataURL('image/jpeg');
      if (shouldStore) {
        capturedFramesRef.current.push(dataURL);
      }
      return dataURL;
    }
    return null;
  };

  const headPoseToAngle = (roll: number, pitch: number, yaw: number): number => {
    const pitchRad = (pitch * Math.PI) / 180;
    const yawRad = (yaw * Math.PI) / 180;
    const x = Math.sin(yawRad);
    const y = -Math.sin(pitchRad);
    let angle = (Math.atan2(y, x) * 180) / Math.PI;
    angle = (angle + 360) % 360;
    return angle;
  };

  const drawLineAtAngle = (angleDeg: number, color: string, ctxBarIndicator: CanvasRenderingContext2D) => {
    const angleRad = (angleDeg * Math.PI) / 180;
    const x1 = centerXRef.current + (radiusXRef.current + gap/2) * Math.cos(angleRad);
    const y1 = centerYRef.current + (radiusYRef.current + gap/2) * Math.sin(angleRad);
    const x2 = centerXRef.current + (radiusXRef.current + gap) * Math.cos(angleRad);
    const y2 = centerYRef.current + (radiusYRef.current + gap) * Math.sin(angleRad);

    ctxBarIndicator.beginPath();
    ctxBarIndicator.moveTo(x1, y1);
    ctxBarIndicator.lineTo(x2, y2);
    ctxBarIndicator.strokeStyle = color;
    ctxBarIndicator.lineWidth = 5;
    ctxBarIndicator.stroke();
  };

  const animateFillSegment = (
    segmentIndex: number,
    endAngle: number,
    ctxBar: CanvasRenderingContext2D,
  ) => {
    const startAngle = segmentIndex * angleStep;
    const endAngleLimit = startAngle + angleStep;

    if (endAngle >= endAngleLimit) {
      if (endAngle === endAngleLimit) {
        filledSegmentsRef.current.add(segmentIndex);
        if (filledSegmentsRef.current.size > segments) {
          // All segments filled - capture complete
          setCaptureComplete(true);
          // Hide video and canvas elements
          if (videoRef.current) {
            videoRef.current.style.display = 'none';
          }
          if (overlayRef.current) {
            overlayRef.current.style.display = 'none';
          }
          if (overlayBarRef.current) {
            overlayBarRef.current.style.display = 'none';
          }
          if (overlayBarIndicatorRef.current) {
            overlayBarIndicatorRef.current.style.display = 'none';
          }
          return;
        }
        return;
      }
      endAngle = endAngleLimit;
    } else {
      endAngle += SEGMENT_FILL_SPEED;
    }

    // Progress along this segment [0,1]
    const rawProgress = (endAngle - startAngle) / angleStep;
    const progress = Math.min(Math.max(rawProgress, 0), 1);

    // Draw a cluster of radial ticks that \"grow\" outward from the outer circle,
    // similar to Face ID. First draw neutral gray placeholders, then overlay green
    // ticks representing progress.
    const baseOuterRx = radiusXRef.current + gap;
    const baseOuterRy = radiusYRef.current + gap;
    const baseOuterRxWithOffset = baseOuterRx + CIRCULAR_SEGMENT_OFFSET;
    const baseOuterRyWithOffset = baseOuterRy + CIRCULAR_SEGMENT_OFFSET;

    // Animated green ticks filling over the placeholders
    for (let i = 0; i < TICKS_PER_SEGMENT; i += 1) {
      const segmentStartFrac = i / TICKS_PER_SEGMENT;
      const local = (progress - segmentStartFrac) * TICKS_PER_SEGMENT;
      const lengthFactor = Math.min(Math.max(local, 0), 1);
      if (lengthFactor <= 0) continue;

      const centerFrac = (i + 0.5) / TICKS_PER_SEGMENT;
      const angle = startAngle + centerFrac * angleStep;

      const innerRx = baseOuterRxWithOffset;
      const innerRy = baseOuterRyWithOffset;
      const outerRx = baseOuterRxWithOffset + MAX_GREEN_TICK_LENGTH * lengthFactor;
      const outerRy = baseOuterRyWithOffset + MAX_GREEN_TICK_LENGTH * lengthFactor;

      const innerX = centerXRef.current + innerRx * Math.cos(angle);
      const innerY = centerYRef.current + innerRy * Math.sin(angle);
      const outerX = centerXRef.current + outerRx * Math.cos(angle);
      const outerY = centerYRef.current + outerRy * Math.sin(angle);

      ctxBar.beginPath();
      ctxBar.moveTo(innerX, innerY);
      ctxBar.lineTo(outerX, outerY);
      ctxBar.strokeStyle = '#22C55E';
      ctxBar.lineWidth = 4;
      ctxBar.lineCap = 'round';
      ctxBar.stroke();
    }

    animationFrameRef.current = requestAnimationFrame(() =>
      animateFillSegment(segmentIndex, endAngle, ctxBar),
    );
  };

  const postProcess = (
    roll: number,
    pitch: number,
    yaw: number,
    ctxBar: CanvasRenderingContext2D | null,
    ctxBarIndicator: CanvasRenderingContext2D | null,
  ) => {
    if (!ctxBar || !ctxBarIndicator) return;

    const angle = headPoseToAngle(roll, pitch, yaw);
    const normalized_angle = (angle - 180) % 360;
    const deviation = Math.sqrt(pitch ** 2 + yaw ** 2);

    if (deviation > DEVIATION_THRESHOLD) {
      const angleRad = ((normalized_angle % 360) * Math.PI) / 180;
      const currentSegmentIndex = Math.floor(angleRad / angleStep);
      let currentSegmentMap = currentSegmentIndex;
      if (currentSegmentMap < 0) {
        currentSegmentMap += segments;
      }

      const segmentFrameCount = segmentFrameCountRef.current;
      segmentFrameCount[currentSegmentMap]++;
      segmentFrameCount.forEach((_, segmentIndex) => {
        if (segmentIndex !== currentSegmentMap) {
          segmentFrameCount[segmentIndex] = 0;
        }
      });

      if (segmentFrameCount[currentSegmentMap] > TILT_FRAME_COUNT) {
        if (
          !savedSegmentsRef.current.has(currentSegmentMap) &&
          !pendingCaptureSegmentsRef.current.has(currentSegmentMap)
        ) {
          pendingCaptureSegmentsRef.current.add(currentSegmentMap);
          segmentFrameCount[currentSegmentMap] = 0;
          void (async () => {
            const frame = saveFrame();
            if (!frame) {
              pendingCaptureSegmentsRef.current.delete(currentSegmentMap);
              cautionSegmentRef.current = currentSegmentMap;
              markFaceIdCaptureFailed();
              setInstructionText(getNextDirectionInstruction());
              return;
            }

            try {
              const descriptors = await computeDescriptorsFromImages([frame]);
              if (descriptors.length === 0) {
                capturedFramesRef.current.pop();
                cautionSegmentRef.current = currentSegmentMap;
                markFaceIdCaptureFailed();
                setInstructionText(getNextDirectionInstruction());
                return;
              }

              savedSegmentsRef.current.add(currentSegmentMap);
              if (cautionSegmentRef.current === currentSegmentMap) {
                cautionSegmentRef.current = null;
              }
              clearFaceIdCaptureWarningNow();
              capturedDescriptorsRef.current.push(descriptors[0]);
              if (onCaptureComplete) {
                onCaptureComplete(capturedFramesRef.current, capturedDescriptorsRef.current);
              }

              const startAngle = currentSegmentMap * angleStep;
              const endAngle = startAngle;
              requestAnimationFrame(() => animateFillSegment(currentSegmentMap, endAngle, ctxBar));
            } catch (error) {
              capturedFramesRef.current.pop();
              cautionSegmentRef.current = currentSegmentMap;
              markFaceIdCaptureFailed();
              setInstructionText(getNextDirectionInstruction());
              console.warn('Failed to capture face descriptor for segment:', currentSegmentMap, error);
            } finally {
              pendingCaptureSegmentsRef.current.delete(currentSegmentMap);
            }
          })();
        }
      }
      currentTiltSegmentRef.current = currentSegmentMap;
      drawLineAtAngle(normalized_angle, '#F87171', ctxBarIndicator);
    } else {
      currentTiltSegmentRef.current = null;
    }
  };

  const onResults = (results: any) => {
    const canvas = overlayRef.current;
    const overlayBar = overlayBarRef.current;
    const overlayBarIndicator = overlayBarIndicatorRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const ctxBar = overlayBar?.getContext('2d');
    const ctxBarIndicator = overlayBarIndicator?.getContext('2d');
    if (!ctx) return;

    ctx.save();
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctxBarIndicator?.clearRect(0, 0, overlayBarIndicator?.width || 0, overlayBarIndicator?.height || 0);

    let faceInsideOval = false;
    let faceStraight = false;
    let roll = 0;
    let pitch = 0;
    let yaw = 0;

    if (results.multiFaceLandmarks && results.multiFaceLandmarks.length > 0) {
      const origLandmarks = results.multiFaceLandmarks[0];
      const landmarks = origLandmarks.map((p: any) => ({
        ...p,
        x: 1 - p.x,
      }));

      ({ roll, pitch, yaw } = getFaceAngles(origLandmarks));

      const xs = landmarks.map((p: any) => p.x * canvas.width);
      const ys = landmarks.map((p: any) => p.y * canvas.height);
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);

      const faceWidth = maxX - minX;
      const faceHeight = maxY - minY;

      const FACEMESH_FACE_OVAL = (window as any).FACEMESH_FACE_OVAL || [];
      const faceWithinOval = FACEMESH_FACE_OVAL.every(([startIdx]: [number, number]) => {
        const point = landmarks[startIdx];
        const x = point.x * canvas.width;
        const y = point.y * canvas.height;
        const marginedRadiusX = (radiusXRef.current + gap) * (1 + FACE_OVAL_MARGIN);
        const marginedRadiusY = (radiusYRef.current + gap) * (1 + FACE_OVAL_MARGIN);
        const normX = (x - centerXRef.current + gap) / marginedRadiusX;
        const normY = (y - centerYRef.current + gap) / marginedRadiusY;
        return normX * normX + normY * normY <= 1;
      });

      const meetsSizeRequirement =
        faceWidth >= radiusXRef.current * 0.5 && faceHeight >= radiusYRef.current * 0.5;
      faceStraight = Math.abs(roll) < ROLL_THRESHOLD;
      faceInsideOval = faceWithinOval && meetsSizeRequirement;

      // Draw face mesh wireframe (exactly like original)
      if ((window as any).drawConnectors && (window as any).FACEMESH_TESSELATION) {
        (window as any).drawConnectors(ctx, landmarks, (window as any).FACEMESH_TESSELATION, {
          color: 'rgba(255,255,255,0.25)',
          lineWidth: 1,
        });
      }
      
      // Don't draw any bounding boxes or rectangles - just the wireframe mesh
    }

    // If the face leaves the oval during the initial "Position Your Face" step,
    // reset the initial caution state so it doesn't stay yellow when they return.
    if (!positionFaceCompleteRef.current && positionFaceDescriptorFailedRef.current && !faceInsideOval) {
      positionFaceDescriptorFailedRef.current = false;
    }

    // Display roll/pitch/yaw for debugging (like original) - commented out for production
    // Uncomment below for debugging
    // const deviation = Math.sqrt(pitch ** 2 + yaw ** 2);
    // ctx.font = '18px Arial';
    // ctx.fillStyle = 'red';
    // ctx.fillText(`Roll: ${roll.toFixed(1)}`, canvas.width / 2 - 60, 120);
    // ctx.fillText(`Pitch: ${pitch.toFixed(1)}`, canvas.width / 2 - 60, 140);
    // ctx.fillText(`Yaw: ${yaw.toFixed(1)}`, canvas.width / 2 - 60, 160);
    // ctx.fillText(`Deviation: ${deviation.toFixed(1)}`, canvas.width / 2 - 60, 180);

    const threshold = Math.min(videoFrameRateRef.current, 30);
    const segmentFrameCount = segmentFrameCountRef.current;

    if (segmentFrameCount[segments] <= threshold) {
      if (isFrameDark()) {
        if (videoInstructionIconRef.current) {
          videoInstructionIconRef.current.style.display = 'none';
        }
        if (videoInstructionImageRef.current && positionFaceImgRef.current) {
          videoInstructionImageRef.current.src = positionFaceImgRef.current.src;
        }
        setInstructionText('Environment is too dark', true);
        segmentFrameCount[segments] = 0;
      } else if (faceInsideOval) {
        if (
          !(
            Math.abs(roll) < ROLL_THRESHOLD &&
            Math.abs(pitch) < PITCH_THRESHOLD &&
            Math.abs(yaw) < YAW_THRESHOLD
          )
        ) {
          if (videoInstructionIconRef.current) {
            videoInstructionIconRef.current.style.display = 'none';
          }
          if (videoInstructionImageRef.current && moveFaceImgRef.current) {
            videoInstructionImageRef.current.src = moveFaceImgRef.current.src;
          }
          setInstructionText('Position Your Face');
          segmentFrameCount[segments] = 0;
        } else {
          segmentFrameCount[segments] += 1;
        }
      } else {
        segmentFrameCount[segments] = 0;
        if (videoInstructionIconRef.current) {
          videoInstructionIconRef.current.style.display = 'none';
        }
        if (videoInstructionImageRef.current && positionFaceImgRef.current) {
          videoInstructionImageRef.current.src = positionFaceImgRef.current.src;
        }
        setInstructionText('Position Your Face');
      }
    }

    if (segmentFrameCount[segments] > threshold) {
      if (!filledSegmentsRef.current.has(segments) && !positionFaceDescriptorPendingRef.current) {
        positionFaceDescriptorPendingRef.current = true;

        const frame = saveFrame();
        if (!frame) {
          positionFaceDescriptorPendingRef.current = false;
          return;
        }

        void (async () => {
          try {
            const descriptors = await computeDescriptorsFromImages([frame]);
            if (descriptors.length === 0) {
              capturedFramesRef.current.pop();
              filledSegmentsRef.current.delete(segments);
              positionFaceCompleteRef.current = false;
              positionFaceDescriptorFailedRef.current = true;
              markFaceIdCaptureFailed();
              segmentFrameCount[segments] = 0;
              setInstructionText('Position Your Face');

              // Revert instruction UI to "Position Your Face"
              if (videoInstructionIconRef.current) {
                videoInstructionIconRef.current.style.display = 'none';
              }
              if (videoInstructionImageRef.current && positionFaceImgRef.current) {
                videoInstructionImageRef.current.style.display = 'block';
                videoInstructionImageRef.current.src = positionFaceImgRef.current.src;
              }
              return;
            }

            positionFaceDescriptorFailedRef.current = false;
            clearFaceIdCaptureWarningNow();
            if (videoInstructionTextRef.current) {
              setInstructionText('Position Your Face');
            }
            capturedDescriptorsRef.current.push(descriptors[0]);
            filledSegmentsRef.current.add(segments);

            if (videoInstructionImageRef.current && videoInstructionIconRef.current) {
              videoInstructionImageRef.current.style.display = 'none';
              videoInstructionIconRef.current.style.display = 'block';

              const positionFaceAnimation = () => {
                setTimeout(() => {
                  positionFaceCompleteRef.current = true;
                }, 100);
                if (videoInstructionIconRef.current) {
                  videoInstructionIconRef.current.removeEventListener(
                    'animationend',
                    positionFaceAnimation,
                  );
                  videoInstructionIconRef.current.style.display = 'none';
                }
                if (videoInstructionImageRef.current && moveFaceImgRef.current) {
                  videoInstructionImageRef.current.style.display = 'block';
                  videoInstructionImageRef.current.src = moveFaceImgRef.current.src;
                }
                if (videoInstructionTextRef.current) {
                  setInstructionText(getNextDirectionInstruction());
                }
              };

              videoInstructionIconRef.current.addEventListener('animationend', positionFaceAnimation);
            }
          } catch (error) {
            // Keep arrays aligned: remove the captured frame if we failed to produce descriptors.
            capturedFramesRef.current.pop();
            filledSegmentsRef.current.delete(segments);
            positionFaceCompleteRef.current = false;
            positionFaceDescriptorFailedRef.current = true;
            markFaceIdCaptureFailed();
            segmentFrameCount[segments] = 0;
            setInstructionText('Position Your Face');
            console.warn('[Stage2Camera] Failed to capture initial face descriptor', error);
          } finally {
            positionFaceDescriptorPendingRef.current = false;
          }
        })();
      }
    }

    const centerX = centerXRef.current;
    const centerY = centerYRef.current;
    const radiusX = radiusXRef.current;
    const radiusY = radiusYRef.current;

    if (positionFaceCompleteRef.current) {
      ctx.beginPath();
      ctx.rect(0, 0, canvas.width, canvas.height);
      ctx.ellipse(centerX, centerY, radiusX + gap, radiusY + gap, 0, 0, 2 * Math.PI);
      ctx.fillStyle = 'rgba(0,0,0,1)';
      ctx.fill('evenodd');
      ctx.restore();
      const baseOuterRx = radiusX + gap;
      const baseOuterRy = radiusY + gap;
      const baseOuterRxWithOffset = baseOuterRx + CIRCULAR_SEGMENT_OFFSET;
      const baseOuterRyWithOffset = baseOuterRy + CIRCULAR_SEGMENT_OFFSET;

      // Placeholder gray ticks for this segment (always visible, half the max green length)
      for (let i = 0; i < TICKS_PER_SEGMENT * segments; i += 1) {
        const centerFrac = (i + 0.5) / TICKS_PER_SEGMENT;
        const angle = 0 + centerFrac * angleStep;
  
        const innerRx = baseOuterRxWithOffset;
        const innerRy = baseOuterRyWithOffset;
        const outerRx = baseOuterRxWithOffset + MAX_GREEN_TICK_LENGTH * 0.5;
        const outerRy = baseOuterRyWithOffset + MAX_GREEN_TICK_LENGTH * 0.5;
  
        const innerX = centerXRef.current + innerRx * Math.cos(angle);
        const innerY = centerYRef.current + innerRy * Math.sin(angle);
        const outerX = centerXRef.current + outerRx * Math.cos(angle);
        const outerY = centerYRef.current + outerRy * Math.sin(angle);
  
        ctx.beginPath();
        ctx.moveTo(innerX, innerY);
        ctx.lineTo(outerX, outerY);
        const segmentIndex = Math.floor(i / TICKS_PER_SEGMENT);
        const showCaution =
          cautionSegmentRef.current === segmentIndex &&
          currentTiltSegmentRef.current === segmentIndex;
        ctx.strokeStyle = showCaution ? '#EAB308' : '#4b5563'; // yellow-500 : gray-600
        ctx.lineWidth = 3;
        ctx.lineCap = 'round';
        ctx.stroke();
      }

      if (isFrameDark()) {
        if (videoInstructionIconRef.current) {
          videoInstructionIconRef.current.style.display = 'none';
        }
        if (videoInstructionImageRef.current && positionFaceImgRef.current) {
          videoInstructionImageRef.current.src = positionFaceImgRef.current.src;
        }
        setInstructionText('Environment is too dark', true);
      } else if (faceInsideOval) {
        if (videoInstructionImageRef.current && moveFaceImgRef.current) {
          videoInstructionImageRef.current.src = moveFaceImgRef.current.src;
        }
        if (videoInstructionIconRef.current) {
          videoInstructionIconRef.current.style.display = 'none';
        }
        if (!faceStraight) {
          setInstructionText('Position Your Face');
        } else {
          setInstructionText(getNextDirectionInstruction());
          if (ctxBar && ctxBarIndicator) {
            postProcess(roll, pitch, yaw, ctxBar, ctxBarIndicator);
          }
        }
      } else {
        setInstructionText('Keep your face inside the circle');
        if (videoInstructionImageRef.current && positionFaceImgRef.current) {
          videoInstructionImageRef.current.src = positionFaceImgRef.current.src;
        }
      }
    } else {
      const cornerRadius = 10;
      const edgeGap = 60;
      const g = edgeGap;
      const r = cornerRadius;
      const halfWidth = 0.75 * radiusX;
      const left = centerX - halfWidth;
      const top = centerY - halfWidth;
      const right = centerX + halfWidth;
      const bottom = centerY + halfWidth;

      ctx.beginPath();
      ctx.moveTo(left + r, top);
      ctx.arcTo(left, top, left, top + r, r);
      ctx.moveTo(left, top + r);
      ctx.lineTo(left, centerY - g);
      ctx.moveTo(left, centerY + g);
      ctx.lineTo(left, bottom - r);
      ctx.arcTo(left, bottom, left + r, bottom, r);
      ctx.lineTo(centerX - g, bottom);
      ctx.moveTo(centerX + g, bottom);
      ctx.lineTo(right - r, bottom);
      ctx.arcTo(right, bottom, right, bottom - r, r);
      ctx.lineTo(right, centerY + g);
      ctx.moveTo(right, centerY - g);
      ctx.lineTo(right, top + r);
      ctx.arcTo(right, top, right - r, top, r);
      ctx.lineTo(centerX + g, top);
      ctx.moveTo(centerX - g, top);
      ctx.lineTo(left + r, top);

      ctx.strokeStyle = positionFaceDescriptorFailedRef.current && faceInsideOval ? '#EAB308' : '#FFFFFF';
      ctx.lineWidth = 5;
      ctx.stroke();

      // Draw dark semi-transparent mask outside the corner brackets
      const displacement = 25;
      ctx.beginPath();
      ctx.rect(0, 0, canvas.width, canvas.height);
      ctx.rect(
        left - displacement,
        top - displacement,
        right - left + displacement * 2,
        bottom - top + displacement * 2,
      );

      ctx.fillStyle = 'rgba(0,0,0,1)';
      ctx.fill('evenodd');
      ctx.restore();
    }
  };

  const viewInstruction = () => {
    setShowGetStarted(false);
    setShowInstructions(true);
  };

  const nextInstruction = () => {
    if (currentInstructionPage >= INSTRUCTION_TITLE.length - 1) {
      if (faceModelLoading || cameraLoading) return;

      setFaceModelError(null);
      setShowInstructions(false);
      setShowGetStarted(false);
      setFaceModelLoading(true);

      void (async () => {
        try {
          await preloadFaceModels();
          setFaceModelLoading(false);
          setCameraLoading(true);
          startCamera();
        } catch (error) {
          console.error('[Stage2Camera] Failed to preload face models', error);
          setFaceModelLoading(false);
          setFaceModelError('Unable to load face model. Please try again.');
          setCameraLoading(false);
          // Restore instruction UI so the user can retry.
          setShowInstructions(true);
          setShowGetStarted(false);
        }
      })();
      return;
    }
    if (currentInstructionPage < INSTRUCTION_TITLE.length - 1) {
      setCurrentInstructionPage((prev) => prev + 1);
    }
  };

  const prevInstruction = () => {
    if (currentInstructionPage > 0) {
      setCurrentInstructionPage((prev) => prev - 1);
    }
  };

  const skipInstruction = () => {
    if (faceModelLoading || cameraLoading) return;
    const confirmedSkip = window.confirm(
      'These instructions are important to understand the process. Are you sure you want to skip?',
    );
    if (!confirmedSkip) return;

    setFaceModelError(null);
    setShowInstructions(false);
    setShowGetStarted(false);
    setFaceModelLoading(true);

    void (async () => {
      try {
        await preloadFaceModels();
        setFaceModelLoading(false);
        setCameraLoading(true);
        startCamera();
      } catch (error) {
        console.error('[Stage2Camera] Failed to preload face models', error);
        setFaceModelLoading(false);
        setFaceModelError('Unable to load face model. Please try again.');
        setCameraLoading(false);
        // Restore the initial screen so the user has a clear next action.
        setShowInstructions(false);
        setShowGetStarted(true);
      }
    })();
  };

  const showHomeUI = showGetStarted || showInstructions;
  const collapseContentWhileLoadingCamera = !showVideo && !showHomeUI;

  const activePreviewFrame = INSTRUCTION_PREVIEW_FRAMES[instructionPreviewFrame];
  const previewInstructionIconSrc =
    instructionPreviewPhase === 'position' ? '/images/position-face-black.png' : '/images/move-face-black.png';
  const previewInstructionText =
    instructionPreviewComplete
      ? 'Face Capture Complete'
      : instructionPreviewPhase === 'position'
      ? 'Position Your Face'
      : activePreviewFrame.instruction;
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (cameraRef.current) {
        cameraRef.current.stop();
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (faceIdCaptureWarningClearTimeoutRef.current) {
        window.clearTimeout(faceIdCaptureWarningClearTimeoutRef.current);
      }
      stopInstructionPreview();
    };
  }, [stopInstructionPreview]);

  // Ensure camera card is visible after transitioning from instructions.
  useEffect(() => {
    if (!showVideo) return;
    const scrollToCamera = () => {
      videoContainerRef.current?.scrollIntoView({
        block: 'start',
        inline: 'nearest',
        behavior: 'smooth',
      });
    };
    const rafId = window.requestAnimationFrame(scrollToCamera);
    return () => window.cancelAnimationFrame(rafId);
  }, [showVideo]);

  return (
    <div className={styles.mainWrapper}>
      <Script
        src="https://cdn.jsdelivr.net/npm/@mediapipe/camera_utils/camera_utils.js"
        strategy="afterInteractive"
        onLoad={() => {
          console.log('Camera utils loaded');
          setScriptsLoaded(true);
        }}
      />
      <Script
        src="https://cdn.jsdelivr.net/npm/@mediapipe/control_utils/control_utils.js"
        strategy="afterInteractive"
        onLoad={() => console.log('Control utils loaded')}
      />
      <Script
        src="https://cdn.jsdelivr.net/npm/@mediapipe/drawing_utils/drawing_utils.js"
        strategy="afterInteractive"
        onLoad={() => console.log('Drawing utils loaded')}
      />
      <Script
        src="https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/face_mesh.js"
        strategy="afterInteractive"
        onLoad={() => {
          console.log('Face mesh loaded');
          setScriptsLoaded(true);
        }}
      />

      {faceModelLoading && (
        <div className={styles.cameraLoading} id="face-model-loading">
          Loading Face Model
          <br />
          Please wait...
        </div>
      )}

      {faceModelError && !faceModelLoading && (
        <div className={styles.cameraLoading} id="face-model-error">
          {faceModelError}
        </div>
      )}

      {cameraLoading && (
        <div className={styles.cameraLoading} id="camera-loading">
          Starting Camera
          <br />
          Please wait...
        </div>
      )}

      <div
        className={`${styles.contentWrapper} ${
          collapseContentWhileLoadingCamera ? styles.contentWrapperLoadingCamera : ''
        }`}
      >
        <div
          className={styles.homeContainer}
          id="home-container"
          style={{ display: showVideo || !showHomeUI ? 'none' : 'flex' }}
        >
          {showGetStarted && (
            <div className={styles.getStartedPage}>
              <div className={styles.headingText}>&ldquo;Let&apos;s Get You Ready&rdquo;</div>
              <div className={styles.headingDescription}>
                We&apos;ll guide you through a few simple steps to prepare you for the face scan.
              </div>
              <button type="button" className={styles.button} onClick={viewInstruction}>
                View Instructions
              </button>
            </div>
          )}

          {showInstructions && (
            <div className={styles.instructionPage}>
              <div className={styles.instructionContent}>
                <div className={styles.instructionMainContent}>
                  <div className={styles.instructionTextSection}>
                    <button
                      type="button"
                      className={`${styles.navBtn} ${styles.skipBtn} ${
                        currentInstructionPage === INSTRUCTION_TITLE.length - 1 ? styles.hidden : ''
                      }`}
                      onClick={skipInstruction}
                    >
                      Skip
                      <svg width="10" height="16" viewBox="0 0 10 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M1.5 1L8.5 8L1.5 15"
                          stroke="#666666"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                    <div className={styles.instructionHeadingContainer}>
                      <div className={styles.instructionHeading}>Instruction</div>
                      <div className={styles.instructionPageNo}>
                        <span className={styles.currentPage}>{currentInstructionPage + 1}</span> of{' '}
                        {INSTRUCTION_TITLE.length}
                      </div>
                      <div className={styles.instructionTitle}>
                        {INSTRUCTION_TITLE[currentInstructionPage]}
                      </div>
                    </div>
                    <div className={styles.instructionDescription}>
                      {INSTRUCTION_DESCRIPTION[currentInstructionPage]}
                    </div>
                    <div className={styles.pageIndicatorContainer}>
                      {INSTRUCTION_TITLE.map((_, idx) => (
                        <div
                          key={idx}
                          id={`page-indicator-${idx + 1}`}
                          className={`${styles.pageIndicator} ${
                            idx === currentInstructionPage ? styles.pageIndicatorActive : ''
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                  <div className={styles.instructionImageContainer}>
                    {currentInstructionPage === INSTRUCTION_PREVIEW_PAGE_INDEX ? (
                      <div className={styles.instructionPreviewContainer}>
                        <div className={styles.instructionPreviewStage}>
                          <button
                            type="button"
                            className={styles.instructionPreviewReplayIconButton}
                            onClick={replayInstructionPreview}
                            aria-label="Replay instruction animation"
                            title="Replay animation"
                            disabled={!instructionPreviewAssetsReady}
                          >
                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path
                                d="M3 4V10H9"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M21 20V14H15"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M20.49 9C19.9828 7.56678 19.1209 6.28542 17.9845 5.27687C16.8482 4.26832 15.4745 3.56488 13.9917 3.23214C12.5089 2.89939 10.9652 2.94852 9.50672 3.37412C8.04819 3.79972 6.72256 4.58735 5.65 5.67L3 8M21 16L18.35 18.33C17.2774 19.4126 15.9518 20.2003 14.4933 20.6259C13.0348 21.0515 11.4911 21.1006 10.0083 20.7679C8.52547 20.4351 7.1518 19.7317 6.01547 18.7231C4.87914 17.7146 4.01717 16.4332 3.51 15"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </button>
                          <img
                            className={styles.instructionPreviewFace}
                            src={activePreviewFrame.src}
                            alt="Face capture instruction preview"
                          />
                          {!instructionPreviewAssetsReady && (
                            <div className={styles.instructionPreviewLoading}>Loading preview...</div>
                          )}
                          <canvas
                            ref={instructionPreviewCanvasRef}
                            className={styles.instructionPreviewCanvas}
                            aria-hidden="true"
                          />
                          <div className={styles.instructionPreviewInstructionPill}>
                            <div className={styles.videoInstructionImageWrapper}>
                              <img
                                className={styles.videoInstructionImageIcon}
                                src={previewInstructionIconSrc}
                                alt=""
                              />
                            </div>
                            <div className={styles.videoInstructionText}>{previewInstructionText}</div>
                          </div>
                          {instructionPreviewComplete && (
                            <div className={styles.instructionPreviewCompleteOverlay}>
                              <div className={styles.instructionPreviewCompleteIcon}>
                                <div className={styles.instructionPreviewCompleteIconBg}></div>
                                <div className={styles.instructionPreviewCompleteCheckmark}>
                                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path
                                      d="M7.75 12L10.58 14.83L16.25 9.17004"
                                      stroke="#ffffff"
                                      strokeWidth="1.8"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                </div>
                              </div>
                              <div className={styles.instructionPreviewCompleteText}>Complete</div>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : currentInstructionPage === INSTRUCTION_POSITION_DEMO_PAGE_INDEX ? (
                      <div className={styles.instructionPositionDemoContainer}>
                        <div className={styles.instructionPositionDemoStage}>
                          <img
                            className={styles.instructionPositionDemoFace}
                            src="/images/face_front.png"
                            alt="Face position demo"
                          />
                          <canvas
                            ref={instructionPositionDemoCanvasRef}
                            className={styles.instructionPositionDemoCanvas}
                            aria-hidden="true"
                          />
                          <div className={styles.instructionPositionDemoCrossBadge} aria-hidden="true">
                            <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path
                                d="M5 5L15 15M15 5L5 15"
                                stroke="#ffffff"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                              />
                            </svg>
                          </div>
                          <div className={styles.instructionPositionDemoTickBadge} aria-hidden="true">
                            <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path
                                d="M4.5 10.5L8.2 14.2L15.5 6.8"
                                stroke="#ffffff"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <img
                        id="instruction-image"
                        className={styles.instructionImage}
                        src={INSTRUCTION_IMAGE_SRC_MAP[currentInstructionPage]}
                        alt="Instruction Image"
                      />
                    )}
                  </div>
                  <div className={styles.instructionButtonContainer}>
                    <button
                      id="prev-button"
                      type="button"
                      className={`${styles.button} ${styles.instructionButton} ${
                        currentInstructionPage === 0 ? styles.hidden : ''
                      }`}
                      onClick={prevInstruction}
                    >
                      <svg width="7" height="14" viewBox="0 0 10 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M8.5 15L1.5 8L8.5 1"
                          stroke="#666666"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      Back
                    </button>
                    <button
                      id="next-button"
                      type="button"
                      className={`${styles.button} ${styles.instructionButton} ${styles.nextButton}`}
                      onClick={nextInstruction}
                    >
                      <div id="next-button-text">
                        {currentInstructionPage === INSTRUCTION_TITLE.length - 1
                          ? 'Start'
                          : 'Next'}
                      </div>
                      <svg width="7" height="14" viewBox="0 0 10 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M1.5 1L8.5 8L1.5 15"
                          stroke="#ffffff"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div
          className={styles.videoContainer}
          id="video-container"
          ref={videoContainerRef}
          style={{ display: showVideo ? 'flex' : 'none' }}
        > 
        <div className={styles.videoContainerInner}>
          <video
            id="video"
            ref={videoRef}
            autoPlay
            playsInline
            style={{ border: '1px solid #000000', boxSizing: 'border-box' }}
          />
          <canvas id="overlay" ref={overlayRef} className={styles.overlay} />
          <canvas id="overlay-bar" ref={overlayBarRef} className={`${styles.overlay} ${styles.overlayBar}`} />
          <canvas
            id="overlay-bar-indicator"
            ref={overlayBarIndicatorRef}
            className={`${styles.overlay} ${styles.overlayBarIndicator}`}
          />
          <div id="video-instructions-container" ref={videoInstructionsContainerRef} className={styles.videoInstructionsContainer}>
            <div className={styles.videoInstructionImageWrapper}>
              <img
                id="video-instruction-image"
                ref={videoInstructionImageRef}
                className={styles.videoInstructionImageIcon}
                src="/images/position-face-black.png"
                alt="Instruction Image"
              />
              <div id="video-instruction-icon" ref={videoInstructionIconRef} className={styles.videoInstructionIcon}>
                <div className={styles.videoInstructionIconBg}></div>
                <div className={styles.videoInstructionCheckmark}>
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M7.75 12L10.58 14.83L16.25 9.17004"
                      stroke="#ffffff"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>
            </div>
            <div id="video-instruction-text" ref={videoInstructionTextRef} className={styles.videoInstructionText}>
              Position Your Face
            </div>
          </div>
        </div>
        </div>
      </div>

      {captureComplete && (
        <div className={styles.completedContainer}>
          <div className={styles.completedInnerContainer}>
            <div className={styles.completedIcon}>
              <div className={styles.completedIconBg}></div>
              <div className={styles.completedCheckmark}>
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M7.75 12L10.58 14.83L16.25 9.17004"
                    stroke="#ffffff"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>
            <div>Capture Complete!</div>
            <button
              type="button"
              className={styles.button}
              disabled={isSubmitting || isLoading}
              onClick={async () => {
                if (isSubmitting || isLoading) {
                  return;
                }
                const images = capturedFramesRef.current;
                setIsSubmitting(true);
                try {
                  if (onCaptureComplete) {
                    onCaptureComplete(images, capturedDescriptorsRef.current);
                  }
                  if (onComplete) {
                    // Wait for the async operation to complete
                    const result = onComplete(images);
                    if (result instanceof Promise) {
                      await result;
                    }
                  }
                } catch (error) {
                  console.error('Error in onComplete:', error);
                  setIsSubmitting(false);
                }
                // Note: isSubmitting will be managed by isLoading prop from parent
                // If isLoading becomes false and we're still here, reset isSubmitting
              }}
            >
              {(isSubmitting || isLoading) ? 'Processing...' : 'Submit'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

